import { supabase } from "@/lib/supabase-client";

export type StatusItem = { status: string; count: number; color: string };
export type EntityBreakdown = { total: number; statuses: StatusItem[] };
export type ProjectSnapshotItem = { id: string; name: string; shiftCount: number };

/* ── colour maps (semantic names matching theme keys) ── */

const USER_STATUS_COLORS: Record<string, string> = {
  ONBOARDING: "todo",
  ACTIVE: "complete",
  INACTIVE: "neutral",
  SEPARATE: "attention",
  PENDING: "pending",
};

const CLIENT_STATUS_COLORS: Record<string, string> = {
  ACTIVE: "complete",
  INACTIVE: "neutral",
};

const PROJECT_STATUS_COLORS: Record<string, string> = {
  POTENTIAL: "todo",
  "PENDING APPROVAL": "pending",
  "IN PROGRESS": "pending",
  "ON HOLD": "attention",
  COMPLETED: "complete",
  CONFLICTING: "attention",
  LOST: "neutral",
  CANCELED: "attention",
};

const INVOICE_STATUS_COLORS: Record<string, string> = {
  DRAFT: "todo",
  SENT: "pending",
  PAID: "complete",
  OVERDUE: "attention",
  CANCELLED: "neutral",
};

/* ── generic breakdown: 1 total + N per-status HEAD queries (zero rows) ── */

async function getStatusBreakdown(
  table: string,
  statusValues: string[],
  colorMap: Record<string, string>,
): Promise<EntityBreakdown> {
  const [totalRes, ...statusResults] = await Promise.all([
    supabase.from(table).select("*", { count: "exact", head: true }),
    ...statusValues.map((s) => supabase.from(table).select("*", { count: "exact", head: true }).eq("status", s)),
  ]);

  const total = totalRes.error ? 0 : (totalRes.count ?? 0);

  const statuses = statusValues
    .map((status, i) => ({
      status,
      count: statusResults[i].error ? 0 : (statusResults[i].count ?? 0),
      color: colorMap[status] || "neutral",
    }))
    .filter((s) => s.count > 0)
    .sort((a, b) => b.count - a.count);

  return { total, statuses };
}

/* ── public API ── */

export async function getUserCounts(): Promise<EntityBreakdown> {
  return getStatusBreakdown("users", Object.keys(USER_STATUS_COLORS), USER_STATUS_COLORS);
}

export async function getClientCounts(): Promise<EntityBreakdown> {
  return getStatusBreakdown("clients", Object.keys(CLIENT_STATUS_COLORS), CLIENT_STATUS_COLORS);
}

export async function getProjectCounts(): Promise<EntityBreakdown> {
  return getStatusBreakdown("projects", Object.keys(PROJECT_STATUS_COLORS), PROJECT_STATUS_COLORS);
}

export async function getInvoiceCounts(): Promise<EntityBreakdown> {
  return getStatusBreakdown("invoices", Object.keys(INVOICE_STATUS_COLORS), INVOICE_STATUS_COLORS);
}

export async function getProjectsSnapshot(): Promise<ProjectSnapshotItem[]> {
  const { data, error } = await supabase
    .from("projects")
    .select("id, name, shifts(count)")
    .eq("status", "IN PROGRESS")
    .limit(3);

  if (error || !data) return [];

  return data.map((p: any) => ({
    id: p.id,
    name: p.name,
    shiftCount: p.shifts?.[0]?.count ?? 0,
  }));
}
